import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './OrgAuth.css';

function OrgInviteAccept() {
  const { code } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [org, setOrg] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function loadInvite() {
      try {
        const inviteDoc = await getDoc(doc(db, 'orgInvites', code));

        if (!inviteDoc.exists()) {
          setError('This invite link was not found.');
          setLoading(false);
          return;
        }

        const inviteData = inviteDoc.data();

        if (inviteData.status !== 'open') {
          setError('This invite link has already been used.');
          setLoading(false);
          return;
        }

        setInvite(inviteData);

        const orgDoc = await getDoc(doc(db, 'organizations', inviteData.orgId));

        if (orgDoc.exists()) {
          setOrg({ id: orgDoc.id, ...orgDoc.data() });
        }
      } catch (err) {
        console.error(err);
        setError('Could not load invite.');
      } finally {
        setLoading(false);
      }
    }

    loadInvite();
  }, [code]);

  async function acceptInvite(e) {
    e.preventDefault();

    if (!invite?.email) {
      setError('Invite is missing an email address.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setError('');
      setSaving(true);

      await createUserWithEmailAndPassword(auth, invite.email, password);

      const functions = getFunctions();
      const claimOrgInvite = httpsCallable(functions, 'claimOrgInvite');

      await claimOrgInvite({ inviteCode: code });

      window.location.href = '/org/dashboard';
    } catch (err) {
      console.error(err);

      if (err.code === 'auth/email-already-in-use') {
        setError('An account already exists for this email. Try signing in instead.');
      } else {
        setError(err.message || 'Could not accept invite.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="auth-page org-auth-page">
      <div className="auth-card">
        <div className="auth-logo" onClick={() => navigate('/org')}>
          somebody.gives
        </div>

        {loading ? (
          <p className="auth-sub">Loading invite...</p>
        ) : error && !invite ? (
          <>
            <h1>Invite unavailable</h1>
            <div className="auth-error">{error}</div>
          </>
        ) : (
          <>
            <h1>Create your organization login</h1>

            <p className="auth-sub">
              You’ve been invited to manage{' '}
              <strong>{org?.orgName || invite?.orgName}</strong>.
            </p>

            {error && <div className="auth-error">{error}</div>}

            <form onSubmit={acceptInvite} className="auth-form">
              <div className="form-group">
                <label>Email</label>
                <input value={invite?.email || ''} disabled />
              </div>

              <div className="form-group">
                <label>Create password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                />
              </div>

              <div className="form-group">
                <label>Confirm password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Re-enter password"
                />
              </div>

              <button className="btn-primary btn-full" disabled={saving}>
                {saving ? 'Creating account...' : 'Create login →'}
              </button>
            </form>

            <div className="auth-footer">
              Already created your login?{' '}
              <span onClick={() => navigate('/org/login')}>Sign in</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default OrgInviteAccept;